/**
 * Leviathan Web - Configuration Settings Page
 * Quasar Prime LLC
 *
 * Manage Customer Types, Industries, and Pricing Tiers
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '@/utils/api';
import {
  ArrowLeft,
  Settings,
  Building2,
  Factory,
  Tag,
  Plus,
  Edit2,
  Trash2,
  Check,
  X,
  Loader2,
  Package,
  Award,
} from 'lucide-react';
import { API_URL } from '@/config/api';
import { readApiFailure } from '@/utils/formErrors';

interface CustomerType {
  id: string;
  code: string;
  label: string;
  description?: string;
  isActive: boolean;
  isDefault: boolean;
  sortOrder: number;
}

interface Industry {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  sortOrder: number;
}

interface PricingTier {
  id: string;
  code: string;
  name: string;
  description?: string;
  multiplier?: number;
  isActive: boolean;
  isDefault: boolean;
  sortOrder: number;
}

interface ProductType {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  sortOrder: number;
}

interface ProductGrade {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  sortOrder: number;
}

type TabType = 'customer-types' | 'industries' | 'pricing-tiers' | 'product-types' | 'product-grades';

export default function ConfigurationSettings() {
  const [activeTab, setActiveTab] = useState<TabType>('customer-types');
  const [customerTypes, setCustomerTypes] = useState<CustomerType[]>([]);
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [pricingTiers, setPricingTiers] = useState<PricingTier[]>([]);
  const [productTypes, setProductTypes] = useState<ProductType[]>([]);
  const [productGrades, setProductGrades] = useState<ProductGrade[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Record<string, string>>({});

  // Add state
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (activeTab === 'customer-types') {
        const response = await apiFetch(`${API_URL}/api/configuration/customer-types`);
        if (response.ok) {
          const data = await response.json();
          setCustomerTypes(data.types || []);
        }
      } else if (activeTab === 'industries') {
        const response = await apiFetch(`${API_URL}/api/configuration/industries`);
        if (response.ok) {
          const data = await response.json();
          setIndustries(data.industries || []);
        }
      } else if (activeTab === 'pricing-tiers') {
        const response = await apiFetch(`${API_URL}/api/configuration/pricing-tiers`);
        if (response.ok) {
          const data = await response.json();
          setPricingTiers(data.tiers || []);
        }
      } else if (activeTab === 'product-types') {
        const response = await apiFetch(`${API_URL}/api/configuration/product-types`);
        if (response.ok) {
          const data = await response.json();
          setProductTypes(data.types || []);
        }
      } else if (activeTab === 'product-grades') {
        const response = await apiFetch(`${API_URL}/api/configuration/product-grades`);
        if (response.ok) {
          const data = await response.json();
          setProductGrades(data.grades || []);
        }
      }
    } catch (err) {
      setError('Failed to fetch configuration data');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdd = async () => {
    setError(null);

    try {
      let url = '';
      let body: Record<string, unknown> = {};

      if (activeTab === 'customer-types') {
        url = `${API_URL}/api/configuration/customer-types`;
        body = {
          code: addForm.code,
          label: addForm.label,
          description: addForm.description || undefined,
        };
      } else if (activeTab === 'industries') {
        url = `${API_URL}/api/configuration/industries`;
        body = {
          name: addForm.name,
          description: addForm.description || undefined,
        };
      } else if (activeTab === 'pricing-tiers') {
        url = `${API_URL}/api/configuration/pricing-tiers`;
        body = {
          code: addForm.code,
          name: addForm.name,
          description: addForm.description || undefined,
          multiplier: addForm.multiplier ? parseFloat(addForm.multiplier) : undefined,
        };
      } else if (activeTab === 'product-types') {
        url = `${API_URL}/api/configuration/product-types`;
        body = {
          name: addForm.name,
          description: addForm.description || undefined,
        };
      } else if (activeTab === 'product-grades') {
        url = `${API_URL}/api/configuration/product-grades`;
        body = {
          name: addForm.name,
          description: addForm.description || undefined,
        };
      }

      const response = await apiFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        setSuccess('Item added successfully');
        setShowAddForm(false);
        setAddForm({});
        fetchData();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const failure = await readApiFailure(response, 'Failed to add item');
        setError(failure.message);
      }
    } catch (err) {
      setError('Failed to add item');
    }
  };

  const handleUpdate = async (id: string) => {
    setError(null);

    try {
      let url = '';
      let body: Record<string, unknown> = {};

      if (activeTab === 'customer-types') {
        url = `${API_URL}/api/configuration/customer-types/${id}`;
        body = {
          label: editForm.label,
          description: editForm.description || undefined,
          isActive: editForm.isActive === 'true',
        };
      } else if (activeTab === 'industries') {
        url = `${API_URL}/api/configuration/industries/${id}`;
        body = {
          name: editForm.name,
          description: editForm.description || undefined,
          isActive: editForm.isActive === 'true',
        };
      } else if (activeTab === 'pricing-tiers') {
        url = `${API_URL}/api/configuration/pricing-tiers/${id}`;
        body = {
          name: editForm.name,
          description: editForm.description || undefined,
          multiplier: editForm.multiplier ? parseFloat(editForm.multiplier) : undefined,
          isActive: editForm.isActive === 'true',
        };
      } else if (activeTab === 'product-types') {
        url = `${API_URL}/api/configuration/product-types/${id}`;
        body = {
          name: editForm.name,
          description: editForm.description || undefined,
          isActive: editForm.isActive === 'true',
        };
      } else if (activeTab === 'product-grades') {
        url = `${API_URL}/api/configuration/product-grades/${id}`;
        body = {
          name: editForm.name,
          description: editForm.description || undefined,
          isActive: editForm.isActive === 'true',
        };
      }

      const response = await apiFetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        setSuccess('Item updated successfully');
        setEditingId(null);
        setEditForm({});
        fetchData();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const failure = await readApiFailure(response, 'Failed to update item');
        setError(failure.message);
      }
    } catch (err) {
      setError('Failed to update item');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this item?')) return;

    setError(null);

    try {
      let url = '';
      if (activeTab === 'customer-types') {
        url = `${API_URL}/api/configuration/customer-types/${id}`;
      } else if (activeTab === 'industries') {
        url = `${API_URL}/api/configuration/industries/${id}`;
      } else if (activeTab === 'pricing-tiers') {
        url = `${API_URL}/api/configuration/pricing-tiers/${id}`;
      } else if (activeTab === 'product-types') {
        url = `${API_URL}/api/configuration/product-types/${id}`;
      } else if (activeTab === 'product-grades') {
        url = `${API_URL}/api/configuration/product-grades/${id}`;
      }

      const response = await apiFetch(url, { method: 'DELETE' });

      if (response.ok) {
        setSuccess('Item deleted successfully');
        fetchData();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const failure = await readApiFailure(response, 'Failed to delete item');
        setError(failure.message);
      }
    } catch (err) {
      setError('Failed to delete item');
    }
  };

  const startEdit = (item: CustomerType | Industry | PricingTier | ProductType | ProductGrade) => {
    setEditingId(item.id);
    if ('label' in item) {
      // CustomerType
      setEditForm({
        label: item.label,
        description: item.description || '',
        isActive: String(item.isActive),
      });
    } else if ('multiplier' in item) {
      // PricingTier
      setEditForm({
        name: item.name,
        description: item.description || '',
        multiplier: item.multiplier?.toString() || '',
        isActive: String(item.isActive),
      });
    } else {
      // Industry, ProductType, ProductGrade
      setEditForm({
        name: item.name,
        description: item.description || '',
        isActive: String(item.isActive),
      });
    }
  };

  const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
    { id: 'customer-types', label: 'Customer Types', icon: <Building2 size={16} /> },
    { id: 'industries', label: 'Industries', icon: <Factory size={16} /> },
    { id: 'pricing-tiers', label: 'Pricing Tiers', icon: <Tag size={16} /> },
    { id: 'product-types', label: 'Product Types', icon: <Package size={16} /> },
    { id: 'product-grades', label: 'Product Grades', icon: <Award size={16} /> },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber" />
        <div className="pl-6">
          <Link to="/settings" className="text-text-secondary hover:text-amber font-mono text-sm flex items-center gap-2 mb-3">
            <ArrowLeft size={14} />
            Back to Settings
          </Link>
          <div className="flex items-center gap-3 mt-2">
            <Settings size={28} className="text-amber" />
            <div>
              <h1
                className="text-3xl font-display font-bold text-steel tracking-wide uppercase"
                style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
              >
                Configuration
              </h1>
              <p className="text-text-secondary text-sm font-mono mt-1">
                Manage Customer Types, Industries, and Pricing Tiers
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-error-red/10 border border-error-red/50 text-error-red p-4 font-mono text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)}><X size={16} /></button>
        </div>
      )}
      {success && (
        <div className="bg-success-green/10 border border-success-green/50 text-success-green p-4 font-mono text-sm flex items-center justify-between">
          <span>{success}</span>
          <button onClick={() => setSuccess(null)}><X size={16} /></button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex space-x-1 border-b border-panel-border">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id);
              setShowAddForm(false);
              setEditingId(null);
            }}
            className={`flex items-center space-x-2 px-6 py-3 font-mono text-sm uppercase tracking-wider transition-all ${
              activeTab === tab.id
                ? 'text-amber border-b-2 border-amber bg-amber/5'
                : 'text-text-secondary hover:text-text-primary hover:bg-panel-border/20'
            }`}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="bg-panel-dark border border-panel-border p-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-amber" size={32} />
          </div>
        ) : (
          <>
            {/* Add Button */}
            <div className="flex justify-end mb-6">
              <button
                onClick={() => {
                  setShowAddForm(!showAddForm);
                  setAddForm({});
                }}
                className="flex items-center gap-2 bg-amber hover:bg-amber/90 text-background font-mono text-sm py-2 px-4 uppercase tracking-wider"
              >
                <Plus size={16} />
                Add New
              </button>
            </div>

            {/* Add Form */}
            {showAddForm && (
              <div className="bg-background border border-amber/50 p-4 mb-6">
                <h3 className="text-amber font-mono text-sm uppercase mb-4">Add New Item</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {activeTab === 'customer-types' && (
                    <>
                      <input
                        type="text"
                        placeholder="Code (e.g., COMMERCIAL)"
                        value={addForm.code || ''}
                        onChange={(e) => setAddForm({ ...addForm, code: e.target.value.toUpperCase() })}
                        className="form-input"
                      />
                      <input
                        type="text"
                        placeholder="Label (e.g., Commercial)"
                        value={addForm.label || ''}
                        onChange={(e) => setAddForm({ ...addForm, label: e.target.value })}
                        className="form-input"
                      />
                      <input
                        type="text"
                        placeholder="Description (optional)"
                        value={addForm.description || ''}
                        onChange={(e) => setAddForm({ ...addForm, description: e.target.value })}
                        className="form-input"
                      />
                    </>
                  )}
                  {activeTab === 'industries' && (
                    <>
                      <input
                        type="text"
                        placeholder="Name (e.g., Construction)"
                        value={addForm.name || ''}
                        onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                        className="form-input"
                      />
                      <input
                        type="text"
                        placeholder="Description (optional)"
                        value={addForm.description || ''}
                        onChange={(e) => setAddForm({ ...addForm, description: e.target.value })}
                        className="form-input md:col-span-2"
                      />
                    </>
                  )}
                  {activeTab === 'pricing-tiers' && (
                    <>
                      <input
                        type="text"
                        placeholder="Code (e.g., PREMIUM)"
                        value={addForm.code || ''}
                        onChange={(e) => setAddForm({ ...addForm, code: e.target.value.toUpperCase() })}
                        className="form-input"
                      />
                      <input
                        type="text"
                        placeholder="Name (e.g., Premium)"
                        value={addForm.name || ''}
                        onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                        className="form-input"
                      />
                      <input
                        type="text"
                        placeholder="Description (optional)"
                        value={addForm.description || ''}
                        onChange={(e) => setAddForm({ ...addForm, description: e.target.value })}
                        className="form-input"
                      />
                    </>
                  )}
                  {activeTab === 'product-types' && (
                    <>
                      <input
                        type="text"
                        placeholder="Name (e.g., Diesel)"
                        value={addForm.name || ''}
                        onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                        className="form-input"
                      />
                      <input
                        type="text"
                        placeholder="Description (optional)"
                        value={addForm.description || ''}
                        onChange={(e) => setAddForm({ ...addForm, description: e.target.value })}
                        className="form-input md:col-span-2"
                      />
                    </>
                  )}
                  {activeTab === 'product-grades' && (
                    <>
                      <input
                        type="text"
                        placeholder="Name (e.g., #2, 87, Premium)"
                        value={addForm.name || ''}
                        onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                        className="form-input"
                      />
                      <input
                        type="text"
                        placeholder="Description (optional)"
                        value={addForm.description || ''}
                        onChange={(e) => setAddForm({ ...addForm, description: e.target.value })}
                        className="form-input md:col-span-2"
                      />
                    </>
                  )}
                </div>
                <div className="flex justify-end gap-2 mt-4">
                  <button
                    onClick={() => {
                      setShowAddForm(false);
                      setAddForm({});
                    }}
                    className="bg-panel-border hover:bg-steel/20 text-text-primary font-mono text-sm py-2 px-4 uppercase"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAdd}
                    className="bg-success-green hover:bg-success-green/90 text-background font-mono text-sm py-2 px-4 uppercase"
                  >
                    Save
                  </button>
                </div>
              </div>
            )}

            {/* Customer Types Table */}
            {activeTab === 'customer-types' && (
              <table className="w-full">
                <thead className="border-b border-panel-border">
                  <tr>
                    <th className="text-left px-4 py-3 text-text-secondary text-xs uppercase tracking-widest font-mono">Code</th>
                    <th className="text-left px-4 py-3 text-text-secondary text-xs uppercase tracking-widest font-mono">Label</th>
                    <th className="text-left px-4 py-3 text-text-secondary text-xs uppercase tracking-widest font-mono">Description</th>
                    <th className="text-left px-4 py-3 text-text-secondary text-xs uppercase tracking-widest font-mono">Status</th>
                    <th className="text-right px-4 py-3 text-text-secondary text-xs uppercase tracking-widest font-mono">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {customerTypes.map((type) => (
                    <tr key={type.id} className="border-b border-panel-border/50 hover:bg-amber/5">
                      <td className="px-4 py-3 font-mono text-text-primary">{type.code}</td>
                      <td className="px-4 py-3 font-mono">
                        {editingId === type.id ? (
                          <input
                            type="text"
                            value={editForm.label || ''}
                            onChange={(e) => setEditForm({ ...editForm, label: e.target.value })}
                            className="form-input py-1"
                          />
                        ) : (
                          <span className="text-text-primary">{type.label}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono text-text-secondary text-sm">
                        {editingId === type.id ? (
                          <input
                            type="text"
                            value={editForm.description || ''}
                            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                            className="form-input py-1"
                          />
                        ) : (
                          type.description || '—'
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded font-mono ${type.isActive ? 'text-success-green bg-success-green/10' : 'text-error-red bg-error-red/10'}`}>
                          {type.isActive ? 'Active' : 'Inactive'}
                        </span>
                        {type.isDefault && (
                          <span className="text-xs px-2 py-1 rounded font-mono text-amber bg-amber/10 ml-2">Default</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {editingId === type.id ? (
                          <div className="flex justify-end gap-2">
                            <button onClick={() => handleUpdate(type.id)} className="text-success-green hover:text-success-green/80">
                              <Check size={16} />
                            </button>
                            <button onClick={() => { setEditingId(null); setEditForm({}); }} className="text-text-secondary hover:text-text-primary">
                              <X size={16} />
                            </button>
                          </div>
                        ) : (
                          <div className="flex justify-end gap-2">
                            <button onClick={() => startEdit(type)} className="text-steel hover:text-steel/80">
                              <Edit2 size={16} />
                            </button>
                            <button onClick={() => handleDelete(type.id)} className="text-error-red hover:text-error-red/80">
                              <Trash2 size={16} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {customerTypes.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-text-tertiary font-mono">
                        No customer types configured. Click "Add New" to create one.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}

            {/* Industries Table */}
            {activeTab === 'industries' && (
              <table className="w-full">
                <thead className="border-b border-panel-border">
                  <tr>
                    <th className="text-left px-4 py-3 text-text-secondary text-xs uppercase tracking-widest font-mono">Name</th>
                    <th className="text-left px-4 py-3 text-text-secondary text-xs uppercase tracking-widest font-mono">Description</th>
                    <th className="text-left px-4 py-3 text-text-secondary text-xs uppercase tracking-widest font-mono">Status</th>
                    <th className="text-right px-4 py-3 text-text-secondary text-xs uppercase tracking-widest font-mono">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {industries.map((industry) => (
                    <tr key={industry.id} className="border-b border-panel-border/50 hover:bg-amber/5">
                      <td className="px-4 py-3 font-mono">
                        {editingId === industry.id ? (
                          <input
                            type="text"
                            value={editForm.name || ''}
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                            className="form-input py-1"
                          />
                        ) : (
                          <span className="text-text-primary">{industry.name}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono text-text-secondary text-sm">
                        {editingId === industry.id ? (
                          <input
                            type="text"
                            value={editForm.description || ''}
                            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                            className="form-input py-1"
                          />
                        ) : (
                          industry.description || '—'
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded font-mono ${industry.isActive ? 'text-success-green bg-success-green/10' : 'text-error-red bg-error-red/10'}`}>
                          {industry.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {editingId === industry.id ? (
                          <div className="flex justify-end gap-2">
                            <button onClick={() => handleUpdate(industry.id)} className="text-success-green hover:text-success-green/80">
                              <Check size={16} />
                            </button>
                            <button onClick={() => { setEditingId(null); setEditForm({}); }} className="text-text-secondary hover:text-text-primary">
                              <X size={16} />
                            </button>
                          </div>
                        ) : (
                          <div className="flex justify-end gap-2">
                            <button onClick={() => startEdit(industry)} className="text-steel hover:text-steel/80">
                              <Edit2 size={16} />
                            </button>
                            <button onClick={() => handleDelete(industry.id)} className="text-error-red hover:text-error-red/80">
                              <Trash2 size={16} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {industries.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center text-text-tertiary font-mono">
                        No industries configured. Click "Add New" to create one.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}

            {/* Pricing Tiers Table */}
            {activeTab === 'pricing-tiers' && (
              <table className="w-full">
                <thead className="border-b border-panel-border">
                  <tr>
                    <th className="text-left px-4 py-3 text-text-secondary text-xs uppercase tracking-widest font-mono">Code</th>
                    <th className="text-left px-4 py-3 text-text-secondary text-xs uppercase tracking-widest font-mono">Name</th>
                    <th className="text-left px-4 py-3 text-text-secondary text-xs uppercase tracking-widest font-mono">Description</th>
                    <th className="text-left px-4 py-3 text-text-secondary text-xs uppercase tracking-widest font-mono">Status</th>
                    <th className="text-right px-4 py-3 text-text-secondary text-xs uppercase tracking-widest font-mono">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pricingTiers.map((tier) => (
                    <tr key={tier.id} className="border-b border-panel-border/50 hover:bg-amber/5">
                      <td className="px-4 py-3 font-mono text-text-primary">{tier.code}</td>
                      <td className="px-4 py-3 font-mono">
                        {editingId === tier.id ? (
                          <input
                            type="text"
                            value={editForm.name || ''}
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                            className="form-input py-1"
                          />
                        ) : (
                          <span className="text-text-primary">{tier.name}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono text-text-secondary text-sm">
                        {editingId === tier.id ? (
                          <input
                            type="text"
                            value={editForm.description || ''}
                            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                            className="form-input py-1"
                          />
                        ) : (
                          tier.description || '—'
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded font-mono ${tier.isActive ? 'text-success-green bg-success-green/10' : 'text-error-red bg-error-red/10'}`}>
                          {tier.isActive ? 'Active' : 'Inactive'}
                        </span>
                        {tier.isDefault && (
                          <span className="text-xs px-2 py-1 rounded font-mono text-amber bg-amber/10 ml-2">Default</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {editingId === tier.id ? (
                          <div className="flex justify-end gap-2">
                            <button onClick={() => handleUpdate(tier.id)} className="text-success-green hover:text-success-green/80">
                              <Check size={16} />
                            </button>
                            <button onClick={() => { setEditingId(null); setEditForm({}); }} className="text-text-secondary hover:text-text-primary">
                              <X size={16} />
                            </button>
                          </div>
                        ) : (
                          <div className="flex justify-end gap-2">
                            <button onClick={() => startEdit(tier)} className="text-steel hover:text-steel/80">
                              <Edit2 size={16} />
                            </button>
                            <button onClick={() => handleDelete(tier.id)} className="text-error-red hover:text-error-red/80">
                              <Trash2 size={16} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {pricingTiers.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-text-tertiary font-mono">
                        No pricing tiers configured. Click "Add New" to create one.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}

            {/* Product Types Table */}
            {activeTab === 'product-types' && (
              <table className="w-full">
                <thead className="border-b border-panel-border">
                  <tr>
                    <th className="text-left px-4 py-3 text-text-secondary text-xs uppercase tracking-widest font-mono">Name</th>
                    <th className="text-left px-4 py-3 text-text-secondary text-xs uppercase tracking-widest font-mono">Description</th>
                    <th className="text-left px-4 py-3 text-text-secondary text-xs uppercase tracking-widest font-mono">Status</th>
                    <th className="text-right px-4 py-3 text-text-secondary text-xs uppercase tracking-widest font-mono">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {productTypes.map((type) => (
                    <tr key={type.id} className="border-b border-panel-border/50 hover:bg-amber/5">
                      <td className="px-4 py-3 font-mono">
                        {editingId === type.id ? (
                          <input
                            type="text"
                            value={editForm.name || ''}
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                            className="form-input py-1"
                          />
                        ) : (
                          <span className="text-text-primary">{type.name}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono text-text-secondary text-sm">
                        {editingId === type.id ? (
                          <input
                            type="text"
                            value={editForm.description || ''}
                            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                            className="form-input py-1"
                          />
                        ) : (
                          type.description || '—'
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded font-mono ${type.isActive ? 'text-success-green bg-success-green/10' : 'text-error-red bg-error-red/10'}`}>
                          {type.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {editingId === type.id ? (
                          <div className="flex justify-end gap-2">
                            <button onClick={() => handleUpdate(type.id)} className="text-success-green hover:text-success-green/80">
                              <Check size={16} />
                            </button>
                            <button onClick={() => { setEditingId(null); setEditForm({}); }} className="text-text-secondary hover:text-text-primary">
                              <X size={16} />
                            </button>
                          </div>
                        ) : (
                          <div className="flex justify-end gap-2">
                            <button onClick={() => startEdit(type)} className="text-steel hover:text-steel/80">
                              <Edit2 size={16} />
                            </button>
                            <button onClick={() => handleDelete(type.id)} className="text-error-red hover:text-error-red/80">
                              <Trash2 size={16} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {productTypes.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center text-text-tertiary font-mono">
                        No product types configured. Click "Add New" to create one.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}

            {/* Product Grades Table */}
            {activeTab === 'product-grades' && (
              <table className="w-full">
                <thead className="border-b border-panel-border">
                  <tr>
                    <th className="text-left px-4 py-3 text-text-secondary text-xs uppercase tracking-widest font-mono">Name</th>
                    <th className="text-left px-4 py-3 text-text-secondary text-xs uppercase tracking-widest font-mono">Description</th>
                    <th className="text-left px-4 py-3 text-text-secondary text-xs uppercase tracking-widest font-mono">Status</th>
                    <th className="text-right px-4 py-3 text-text-secondary text-xs uppercase tracking-widest font-mono">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {productGrades.map((grade) => (
                    <tr key={grade.id} className="border-b border-panel-border/50 hover:bg-amber/5">
                      <td className="px-4 py-3 font-mono">
                        {editingId === grade.id ? (
                          <input
                            type="text"
                            value={editForm.name || ''}
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                            className="form-input py-1"
                          />
                        ) : (
                          <span className="text-text-primary">{grade.name}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono text-text-secondary text-sm">
                        {editingId === grade.id ? (
                          <input
                            type="text"
                            value={editForm.description || ''}
                            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                            className="form-input py-1"
                          />
                        ) : (
                          grade.description || '—'
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded font-mono ${grade.isActive ? 'text-success-green bg-success-green/10' : 'text-error-red bg-error-red/10'}`}>
                          {grade.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {editingId === grade.id ? (
                          <div className="flex justify-end gap-2">
                            <button onClick={() => handleUpdate(grade.id)} className="text-success-green hover:text-success-green/80">
                              <Check size={16} />
                            </button>
                            <button onClick={() => { setEditingId(null); setEditForm({}); }} className="text-text-secondary hover:text-text-primary">
                              <X size={16} />
                            </button>
                          </div>
                        ) : (
                          <div className="flex justify-end gap-2">
                            <button onClick={() => startEdit(grade)} className="text-steel hover:text-steel/80">
                              <Edit2 size={16} />
                            </button>
                            <button onClick={() => handleDelete(grade.id)} className="text-error-red hover:text-error-red/80">
                              <Trash2 size={16} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {productGrades.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center text-text-tertiary font-mono">
                        No product grades configured. Click "Add New" to create one.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
    </div>
  );
}
